import { Color3 as BABYLON_Color3, Color4 as BABYLON_Color4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.color.js';
import { IVector3, LineType } from '../../../../api.js';
import { rotate } from '../../../../Math.js';
import { icolor_to_col4, vector_3 } from '../../../utility/Conversion.js';
const PRIMARY_COLOR = BABYLON_Color3.Magenta().toColor4();
const SECONDARY_COLOR = BABYLON_Color3.Green().toColor4();
// If UNIFORM color mode is used, determines how similar the light source color may
// be to PRIMARY_COLOR before switching to SECONDARY_COLOR.
const MIN_COLOR_DIFFERENCE = 0.3;
// Used to make line length appear proportional to light falloff.
const VISIBLE_LIGHT_COEFFICIENT = 4;
const LINE_COLORS = [BABYLON_Color3.Green(), BABYLON_Color3.Magenta(), BABYLON_Color3.Red(),
    BABYLON_Color3.Gray(), BABYLON_Color3.Blue(), BABYLON_Color3.Purple(), BABYLON_Color3.Teal(), BABYLON_Color3.Yellow()];
/**
 * Determines how the lines of LightVisualizers are colored.
 * DIFFERENTIATED (default): Color is set according to LightID and index of line_colors.
 * For LightID's > 7, random color is used.
 * UNIFORM: All LightVisualizers have PRIMARY_COLOR (except if it's very similar
 * to the light source's - then they use SECONDARY_COLOR.
 * MATCHING: Lines are of the same color as the light source.
 */
export var ColorMode;
(function (ColorMode) {
    ColorMode[ColorMode["DIFFERENTIATED"] = 0] = "DIFFERENTIATED";
    ColorMode[ColorMode["UNIFORM"] = 1] = "UNIFORM";
    ColorMode[ColorMode["MATCHING"] = 2] = "MATCHING";
})(ColorMode || (ColorMode = {}));
export class LightVisualizer {
    constructor(_light, _line_manager, _color_mode, _scene) {
        this._light = _light;
        this._line_manager = _line_manager;
        this._color_mode = _color_mode;
        this._scene = _scene;
        this._random_color = BABYLON_Color3.Random();
        this._minimum_line_length = -1;
        // Computed by using intensity and the inverse square law, or shadow_far,
        // if that is smaller
        this._light_range = -1;
        this._lines = [];
        this._intensity_line_thickness = {
            min_intensity: 0.3, min_thickness: 0.005, max_intensity: 5, max_thickness: 0.02
        };
    }
    get color_mode() {
        return this._color_mode;
    }
    set color_mode(mode) {
        this._color_mode = mode;
        this.update();
    }
    /**
     * Remove lines used for visualization. If start_index is set, spare lines with lower index.
     */
    dispose(start_index = 0) {
        // Disposing of sprite will take place here
        this.remove_lines(start_index);
    }
    update_visualizer(light_range_coefficient = VISIBLE_LIGHT_COEFFICIENT) {
        this.set_light_range(light_range_coefficient);
        this._line_color = this.compute_line_color();
        this._line_thickness = this.get_thickness();
    }
    get_thickness() {
        const intensity = this._light.intensity;
        const map = this._intensity_line_thickness;
        const intensity_quotient = (intensity - map.min_intensity) / (map.max_intensity - map.min_intensity);
        const thickness = (map.max_thickness - map.min_thickness) * intensity_quotient + map.min_thickness;
        return Math.min(Math.max(thickness, map.min_thickness), map.max_thickness);
    }
    // If uniform colors are used, determine whether or not PRIMARY_COLOR or SECONDARY_COLOR
    // should be used for the lines. This is in order to clearly visually separate
    // the lines from the lighting.
    get_uniform_color(color) {
        const difference = Math.abs(color.r - PRIMARY_COLOR.r) +
            Math.abs(color.g - PRIMARY_COLOR.g) + Math.abs(color.b - PRIMARY_COLOR.b);
        if (difference >= MIN_COLOR_DIFFERENCE) {
            return PRIMARY_COLOR;
        }
        else
            return SECONDARY_COLOR;
    }
    get_differentiated_color(id) {
        const color = id < LINE_COLORS.length ? LINE_COLORS[id] : this._random_color;
        return BABYLON_Color4.FromColor3(color);
    }
    compute_line_color() {
        const light_color3 = this._light.color;
        const light_id = this._light.id;
        const light_color4 = icolor_to_col4(light_color3);
        switch (this.color_mode) {
            case ColorMode.UNIFORM:
                return this.get_uniform_color(light_color4);
            case ColorMode.DIFFERENTIATED:
                return this.get_differentiated_color(light_id);
            default:
                return light_color4;
        }
    }
    set_light_range(light_range_coefficient) {
        var _a;
        this._light_range = Math.max(Math.sqrt(this._light.intensity * light_range_coefficient), this._minimum_line_length);
        const shadow_far = ((_a = this._light.shadow) === null || _a === void 0 ? void 0 : _a.shadow_far) || 20;
        if (this._light_range > shadow_far) {
            this._light_range = shadow_far;
        }
    }
    draw_line(index, p1, p2, color, line_thickness) {
        if (this._lines.length > index) {
            this._line_manager.update_line(this._lines[index], p1, p2, color, line_thickness);
        }
        else {
            this._lines.push(this._line_manager.create_line(LineType.SOLID, p1, p2, color, line_thickness));
        }
    }
    // Draw curve and return index of last line modified
    draw_curve(index, start_point, endpoint, main_axis, center, delta_angle) {
        const num_segments = Math.round(delta_angle * 100);
        const points = [...Array(num_segments)].map((_, i) => rotate(start_point.subtract(center), -delta_angle * i / num_segments, main_axis));
        for (let i = 0; i < points.length; i++) {
            const point = vector_3(points[i]);
            if (i === points.length - 1) {
                this.draw_line(index + i, point.add(center), endpoint, this._line_color, this._line_thickness);
            }
            else {
                this.draw_line(index + i, point.add(center), vector_3(points[i + 1]).add(center), this._line_color, this._line_thickness);
            }
        }
        return index + num_segments;
    }
    remove_lines(start_index) {
        this._lines.slice(start_index).forEach(line => {
            this._line_manager.remove_line(line);
        });
        this._lines.splice(start_index);
    }
    draw_directional_lines(length, radius, shape, num_lines, light_direction, light_position, far_circle = false) {
        const center_far = light_position.add(light_direction.scale(length));
        const perp_vec = vector_3(IVector3.perpendicular(light_direction));
        const cross_vec = light_direction.cross(perp_vec);
        const polar_step = (Math.PI * 2.0) / num_lines;
        for (let n = 0; n < num_lines; n++) {
            const ct = Math.cos(n * polar_step);
            const st = Math.sin(n * polar_step);
            const offset = perp_vec.scale(ct * radius)
                .add(cross_vec.scale(st * radius));
            const endpoint = center_far.add(offset);
            const start_point = shape === 'cone' ? light_position : light_position.add(offset);
            this.draw_line(n, start_point, endpoint, this._line_color, this._line_thickness);
            if (n === 0 && far_circle) {
                this.draw_curve(num_lines, endpoint, endpoint, light_direction, center_far, 2 * Math.PI);
            }
        }
    }
}
//# sourceMappingURL=LightVisualizer.js.map